import * as React from "react";
import { Helmet } from "react-helmet";
import { Box, Typography, Container, Paper, TextField, Button, Link, Grid, useTheme, useMediaQuery, Divider, Stack, IconButton } from "@mui/material";
import PhoneImage from './images/phone.png';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import RedditIcon from '@mui/icons-material/Reddit';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getAllBlogs } from '../index'; // Import the global blogs function
import Navbar from "../../Navbar";

const THEME = {
    colors: {
        primary: '#2ECC40',
        secondary: '#1a1a1a',
        accent: '#4CAF50',
        background: '#ffffff',
        text: {
            primary: '#1a1a1a',
            secondary: '#666666',
            light: '#888888'
        },
        gradient: {
            green: 'linear-gradient(135deg, #2ECC40 0%, #4CAF50 100%)',
            darkGreen: 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)',
            lightGreen: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
            blue: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
            orange: 'linear-gradient(135deg, #FF9966 0%, #FF5E62 100%)'
        },
        hover: {
            green: 'linear-gradient(135deg, #3DD447 0%, #5CBF60 100%)',
            darkGreen: 'linear-gradient(135deg, #43A047 0%, #388E3C 100%)',
        }
    },
    spacing: {
        section: '3rem',
        element: '1.5rem',
        headerOffset: '64px'
    },
    shadows: {
        card: '0 4px 24px rgba(0,0,0,0.06)',
        green: '0 4px 24px rgba(46,204,64,0.2)',
        darkGreen: '0 4px 24px rgba(76,175,80,0.2)',
        hover: '0 8px 32px rgba(0,0,0,0.12)'
    }
};

const BackButton = () => {
    const navigate = useNavigate();

    return (
        <Button
            onClick={() => navigate('/blogs')}
            startIcon={<ArrowBackIcon />}
            sx={{
                color: THEME.colors.text.secondary,
                fontWeight: 600,
                mb: 3,
                transition: 'all 0.2s ease',
                '&:hover': {
                    color: THEME.colors.primary,
                    transform: 'translateX(-4px)',
                    bgcolor: 'rgba(46,204,64,0.1)'
                }
            }}
        >
            Back to Articles
        </Button>
    );
};

const BlogFooter = () => {
    return (
        <Box sx={{
            bgcolor: '#f8f9fa',
            py: 8,
            borderTop: '1px solid #e9ecef'
        }}>
            <Container maxWidth="md">
                <Box sx={{
                    textAlign: 'center',
                    maxWidth: '800px',
                    mx: 'auto'
                }}>
                    <Typography variant="h3" sx={{
                        fontSize: { xs: '2rem', md: '2.5rem' },
                        fontWeight: 800,
                        color: '#1a1a1a',
                        mb: 2
                    }}>
                        Ready to Start Using Histomart?
                    </Typography>
                    <Typography variant="body1" sx={{
                        fontSize: '1.1rem',
                        color: '#666',
                        mb: 4,
                        mx: 'auto',
                        maxWidth: '600px'
                    }}>
                        Join thousands of users using Histomart to analyze historical data and grow their Walmart business.
                    </Typography>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Button
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => {window.location.href = "/"}}
                            sx={{
                                bgcolor: THEME.colors.primary,
                                color: 'white',
                                py: 1.5,
                                px: 4,
                                '&:hover': {
                                    bgcolor: '#26b83a'
                                }
                            }}
                        >
                            Start Free Trial
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {window.location.href = "/"}}
                            sx={{
                                borderColor: '#1a1a1a',
                                color: '#1a1a1a',
                                py: 1.5,
                                px: 4,
                                '&:hover': {
                                    borderColor: '#1a1a1a',
                                    bgcolor: 'rgba(26,26,26,0.04)'
                                }
                            }}
                        >
                            Learn More
                        </Button>
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
};

// Function to generate social sharing URLs
const generateSocialSharingUrls = (url, title, description) => {
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    const encodedDesc = encodeURIComponent(description);
    
    return {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
        telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
        whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`
    };
};

// Function to get related blog posts
const getRelatedPosts = (currentBlogUrl, category, keywords, count = 3) => {
    const allBlogs = getAllBlogs();
    
    // Filter out current blog and find related ones
    return allBlogs
        .filter(blog => {
            // Skip current blog
            if (blog.url === currentBlogUrl) return false;
            
            // Match by category
            if (category && blog.category === category) return true;
            
            // Match by keywords overlap
            if (keywords && keywords.length && blog.keywords && blog.keywords.length) {
                return blog.keywords.some(keyword => 
                    keywords.includes(keyword)
                );
            }
            
            return false;
        })
        .slice(0, count); // Get requested number of related posts
};

export default function Blog({
    title = "",
    author = "Ido Friedman",
    date,
    heroImage,
    content,
    tableOfContents,
    recommendedPosts,
    meta = {},
    category,
    keywords = []
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [email, setEmail] = React.useState('');
    const [emailError, setEmailError] = React.useState(false);
    const location = useLocation();
    
    // Get base URL for SEO
    const baseUrl = window.location.origin;
    const currentUrl = baseUrl + location.pathname;
    
    // Get related posts if none provided
    const relatedPosts = React.useMemo(() => {
        if (recommendedPosts && recommendedPosts.length > 0) {
            return recommendedPosts;
        }
        
        // Generate related posts automatically
        return getRelatedPosts(location.pathname, category, keywords);
    }, [recommendedPosts, location.pathname, category, keywords]);
    
    // Generate social sharing URLs if not provided
    const socialSharingUrls = React.useMemo(() => {
        if (meta?.socialSharing) {
            return meta.socialSharing;
        }
        
        return generateSocialSharingUrls(
            currentUrl,
            title,
            meta?.description || ""
        );
    }, [currentUrl, title, meta]);

    const isValidEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const joinMailingList = (e) => {
        e.preventDefault();
        if (!isValidEmail(email)) {
            setEmailError(true);
            return;
        }

        console.log('Valid email submitted:', email);
        alert(`Adding ${email} to mailing list`)
        setEmailError(false);
        setEmail('');
    };

    // Format date for schema.org and display
    const publishDate = date ? new Date(date) : new Date();
    const formattedDate = publishDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    const isoDate = publishDate.toISOString();

    return (
        <>
            <Helmet>
                <title>{`${title} | Histomart`}</title>
                <meta name="description" content={String(meta?.description || '')} />
                <meta name="keywords" content={String(meta?.keywords || keywords?.join(', ') || '')} />
                <link rel="canonical" href={String(meta?.canonical || currentUrl)} />
                
                {/* Open Graph Tags */}
                <meta property="og:title" content={title} />
                <meta property="og:description" content={String(meta?.description || '')} />
                <meta property="og:image" content={String(meta?.ogImage || heroImage)} />
                <meta property="og:url" content={String(meta?.canonical || currentUrl)} />
                <meta property="og:type" content="article" />
                <meta property="og:site_name" content="Histomart - Walmart Historical Data Tool" />
                <meta property="article:published_time" content={isoDate} />
                {category && <meta property="article:section" content={category} />}
                {keywords && keywords.map((keyword, index) => (
                    <meta property="article:tag" content={keyword} key={index} />
                ))}
                
                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={String(meta?.description || '')} />
                <meta name="twitter:image" content={String(meta?.ogImage || heroImage)} />
                
                {/* Structured Data for SEO */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BlogPosting",
                        "headline": title,
                        "description": meta?.description || "",
                        "image": heroImage,
                        "datePublished": isoDate,
                        "author": {
                            "@type": "Person",
                            "name": author
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "Histomart",
                            "logo": {
                                "@type": "ImageObject",
                                "url": `${baseUrl}/fulllogot.png`
                            }
                        },
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": meta?.canonical || currentUrl
                        },
                        "keywords": keywords || [],
                        "articleSection": category || "General"
                    })}
                </script>
            </Helmet>

            <Navbar />

            <Box sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: '#f8f9fa',
                pt: { xs: 8, md: 10 },
                pb: { xs: 6, md: 10 },
            }}>
                <Container maxWidth="lg">
                    <BackButton />

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={8}>
                            <Paper elevation={0} sx={{
                                p: { xs: 3, md: 5 },
                                borderRadius: 3,
                                backgroundColor: 'white',
                                boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
                            }}>
                                <Typography variant="h1" sx={{
                                    fontSize: { xs: '2.25rem', md: '3rem' },
                                    fontWeight: 800,
                                    color: '#1a1a1a',
                                    mb: 3,
                                    lineHeight: 1.2
                                }}>
                                    {title}
                                </Typography>

                                {/* Category tag for SEO and UX */}
                                {category && (
                                    <Box sx={{ mb: 3 }}>
                                        <Button
                                            component={Link}
                                            href={`/blogs/category/${category.toLowerCase().replace(/\s+/g, '-')}`}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                borderColor: THEME.colors.primary,
                                                color: THEME.colors.secondary,
                                                bgcolor: 'rgba(46,204,64,0.1)',
                                                borderRadius: '16px',
                                                py: 0.5,
                                                px: 1.5,
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    bgcolor: 'rgba(46,204,64,0.2)',
                                                    borderColor: THEME.colors.primary
                                                }
                                            }}
                                        >
                                            {category}
                                        </Button>
                                    </Box>
                                )}

                                <Box sx={{
                                    mb: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <Typography variant="subtitle1" sx={{
                                        color: THEME.colors.text.secondary,
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5
                                    }}>
                                        By {author}
                                        {date && (
                                            <>
                                                <span style={{
                                                    margin: '0 8px',
                                                    color: THEME.colors.text.light
                                                }}>•</span>
                                                {formattedDate}
                                            </>
                                        )}
                                    </Typography>

                                    <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
                                        {[
                                            { icon: <FacebookIcon />, url: socialSharingUrls.facebook },
                                            { icon: <TwitterIcon />, url: socialSharingUrls.twitter },
                                            { icon: <LinkedInIcon />, url: socialSharingUrls.linkedin },
                                            { icon: <RedditIcon />, url: socialSharingUrls.reddit },
                                            { icon: <TelegramIcon />, url: socialSharingUrls.telegram },
                                            { icon: <WhatsAppIcon />, url: socialSharingUrls.whatsapp }
                                        ].map((social, index) => (
                                            <IconButton
                                                key={index}
                                                href={social.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: THEME.colors.primary,
                                                    padding: '4px',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        bgcolor: 'rgba(46,204,64,0.1)'
                                                    }
                                                }}
                                            >
                                                {social.icon}
                                            </IconButton>
                                        ))}
                                    </Stack>
                                </Box>

                                {heroImage && (
                                    <Box sx={{
                                        mb: 5,
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        boxShadow: '0 6px 32px rgba(0,0,0,0.08)',
                                        width: '100%',
                                        height: { xs: '200px', sm: '256px' },
                                        position: 'relative'
                                    }}>
                                        <img
                                            src={heroImage}
                                            alt={title}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                objectPosition: 'center',
                                                display: 'block'
                                            }}
                                        />
                                    </Box>
                                )}

                                {tableOfContents && tableOfContents.length > 0 && (
                                    <Paper sx={{
                                        p: 4,
                                        mb: 5,
                                        background: 'linear-gradient(to right, #f8f9fa, #ffffff)',
                                        borderRadius: 3,
                                        border: `2px solid ${THEME.colors.primary}`
                                    }}>
                                        <Typography variant="h6" gutterBottom sx={{
                                            fontWeight: 700,
                                            color: THEME.colors.text.primary
                                        }}>
                                            Quick Navigation
                                        </Typography>
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1.5
                                        }}>
                                            {tableOfContents.map((item, index) => (
                                                <Link
                                                    key={index}
                                                    href={`#${item.id}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        const element = document.getElementById(item.id);
                                                        const offset = 80;
                                                        const bodyRect = document.body.getBoundingClientRect().top;
                                                        const elementRect = element.getBoundingClientRect().top;
                                                        const elementPosition = elementRect - bodyRect;
                                                        const offsetPosition = elementPosition - offset;

                                                        window.scrollTo({
                                                            top: offsetPosition,
                                                            behavior: 'smooth'
                                                        });
                                                    }}
                                                    sx={{
                                                        color: THEME.colors.text.primary,
                                                        textDecoration: 'none',
                                                        fontSize: '1.1rem',
                                                        fontWeight: 500,
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            color: THEME.colors.primary,
                                                            pl: 1
                                                        }
                                                    }}
                                                >
                                                    {item.title}
                                                </Link>
                                            ))}
                                        </Box>
                                    </Paper>
                                )}

                                <Box sx={{
                                    '& section': {
                                        mb: 6,
                                        scrollMarginTop: '80px',
                                        '& h4': {
                                            color: THEME.colors.text.primary,
                                            fontWeight: 700,
                                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                                            mb: 3
                                        },
                                        '& p': {
                                            color: THEME.colors.text.secondary,
                                            fontSize: '1.1rem',
                                            lineHeight: 1.8,
                                            mb: 2.5
                                        },
                                        '& img': {
                                            maxWidth: '100%',
                                            height: { xs: '200px', sm: '256px' },
                                            objectFit: 'cover',
                                            objectPosition: 'center',
                                            borderRadius: '16px',
                                            display: 'block',
                                            marginBottom: 2
                                        },
                                        '& .image-container': {
                                            width: '100%',
                                            height: { xs: '200px', sm: '256px' },
                                            position: 'relative',
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            marginBottom: 2
                                        }
                                    }
                                }}>
                                    {content}
                                </Box>
                                
                                {/* Keywords/tags for SEO */}
                                {keywords && keywords.length > 0 && (
                                    <Box sx={{ 
                                        mt: 4, 
                                        pt: 4, 
                                        borderTop: '1px solid rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center'
                                    }}>
                                        <Typography 
                                            variant="subtitle2" 
                                            sx={{ 
                                                fontWeight: 600, 
                                                mr: 2,
                                                color: THEME.colors.text.secondary
                                            }}
                                        >
                                            Tags:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {keywords.map((keyword, index) => (
                                                <Button
                                                    key={index}
                                                    component={Link}
                                                    href={`/blogs/tag/${keyword.toLowerCase().replace(/\s+/g, '-')}`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'rgba(0,0,0,0.05)',
                                                        color: THEME.colors.text.secondary,
                                                        borderRadius: '16px',
                                                        fontSize: '0.75rem',
                                                        textTransform: 'none',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(0,0,0,0.08)'
                                                        }
                                                    }}
                                                >
                                                    {keyword}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box sx={{
                                position: 'sticky',
                                top: 24,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4
                            }}>
                                <Paper sx={{
                                    p: 4,
                                    background: THEME.colors.gradient.green,
                                    borderRadius: 3,
                                    boxShadow: THEME.shadows.green
                                }}>
                                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                                        <img
                                            src={PhoneImage}
                                            alt="Histomart App"
                                            style={{
                                                width: '120px',
                                                height: 'auto',
                                                marginBottom: '16px'
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="h6" gutterBottom align="center" sx={{
                                        color: 'white',
                                        fontWeight: 600
                                    }}>
                                        Looking for Historical Walmart Data Insights?
                                    </Typography>
                                    <Box
                                        component="form"
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                            mt: 2
                                        }}
                                    >
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            fullWidth
                                            onClick={() => window.open("https://www.youtube.com/@getmarter/playlists")}
                                            sx={{
                                                marginTop: '2%',
                                                bgcolor: THEME.colors.secondary,
                                                color: 'white',
                                                py: 1.5,
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    bgcolor: '#333',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: THEME.shadows.hover
                                                }
                                            }}
                                        >
                                            Watch Our Free Content
                                        </Button>
                                    </Box>
                                </Paper>

                                {relatedPosts && relatedPosts.length > 0 && (
                                    <Paper sx={{
                                        p: 4,
                                        borderRadius: 3,
                                        backgroundColor: 'white',
                                        boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
                                    }}>
                                        <Typography variant="h6" gutterBottom sx={{
                                            fontWeight: 700,
                                            color: THEME.colors.text.primary,
                                            mb: 3
                                        }}>
                                            Recommended Reading
                                        </Typography>
                                        <Divider sx={{ mb: 3 }} />
                                        {relatedPosts.map((post, index) => (
                                            <Link
                                                key={index}
                                                href={post.url}
                                                sx={{
                                                    display: 'block',
                                                    color: THEME.colors.text.primary,
                                                    textDecoration: 'none',
                                                    py: 1.5,
                                                    fontSize: '1.05rem',
                                                    fontWeight: 500,
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        color: THEME.colors.primary,
                                                        pl: 1
                                                    }
                                                }}
                                            >
                                                {post.title}
                                            </Link>
                                        ))}
                                    </Paper>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Box sx={{
                bgcolor: '#f8f9fa',
                py: { xs: 6, md: 8 },
                borderBottom: '1px solid #e9ecef'
            }}>
                <Container maxWidth="lg">
                    <Paper sx={{
                        p: { xs: 4, md: 6 },
                        borderRadius: 3,
                        background: THEME.colors.gradient.green,
                        boxShadow: THEME.shadows.green,
                        textAlign: 'center'
                    }}>
                        <Typography variant="h4" gutterBottom sx={{
                            color: "#FFFFFF",
                            fontWeight: 800,
                            mb: 4,
                            fontSize: { xs: '1.75rem', md: '2rem' }
                        }}>
                            Share this blog to get 30% off Histomart
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={{ xs: 1, md: 2 }}
                            justifyContent="center"
                            flexWrap="wrap"
                            sx={{ mb: 4 }}
                        >
                            {[
                                { icon: <FacebookIcon />, url: socialSharingUrls.facebook, color: '#1877f2' },
                                { icon: <TwitterIcon />, url: socialSharingUrls.twitter, color: '#1da1f2' },
                                { icon: <LinkedInIcon />, url: socialSharingUrls.linkedin, color: '#0a66c2' },
                                { icon: <RedditIcon />, url: socialSharingUrls.reddit, color: '#ff4500' },
                                { icon: <TelegramIcon />, url: socialSharingUrls.telegram, color: '#0088cc' },
                                { icon: <WhatsAppIcon />, url: socialSharingUrls.whatsapp, color: '#25d366' }
                            ].map((social, index) => (
                                <IconButton
                                    key={index}
                                    href={social.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        bgcolor: 'rgba(0,0,0,0.05)',
                                        color: "#FFFFFF",
                                        p: { xs: 1.5, md: 2 },
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            bgcolor: 'rgba(0,0,0,0.1)',
                                            color: social.color
                                        }
                                    }}
                                >
                                    {social.icon}
                                </IconButton>
                            ))}
                        </Stack>

                        <Typography
                            variant="body1"
                            sx={{
                                color: "#FFFFFF",
                                fontWeight: 500,
                                fontSize: '1.1rem'
                            }}
                        >
                            Send a screenshot to <strong>team@histomart.com</strong> to get your discount!
                        </Typography>
                    </Paper>
                </Container>
            </Box>

            <BlogFooter />
      <Box sx={{ padding: 8, textAlign: 'center' }}>
        <Typography variant="body1" color="black">
          Contact us at <a href="mailto:team@histomart.com">team@histomart.com</a>
        </Typography>
      </Box>
        </>
    );
}